import React from "react";

/**
 * Displays a pilot's badge level as a hexagon indicator.
 *
 * Standalone:  <Level level={5} size="small" />
 * Badge mode:  <Level level={5} size="large"><Avatar /></Level>
 *              Wraps children and overlays the hexagon at bottom-right,
 *              just like Ant Design's <Badge> but hexagon-shaped.
 *
 * Props:
 *   level    {number}                   Pre-computed level (0 = nothing rendered).
 *   size     {'small'|'default'|'large'} Display size.
 *   children {node}                     Optional — enables Badge wrapper mode.
 */

const LEVEL_COLORS = [
  { level: 1,  color: "#9e9e9e" },  // Iron
  { level: 2,  color: "#cd7f32" },  // Bronze
  { level: 3,  color: "#808080" },  // Silver
  { level: 4,  color: "#c8a800" },  // Gold
  { level: 5,  color: "#0080c0" },  // Sapphire
  { level: 6,  color: "#43a047" },  // Emerald
  { level: 7,  color: "#8e24aa" },  // Amethyst
  { level: 8,  color: "#e53935" },  // Ruby
  { level: 9,  color: "#00acc1" },  // Topaz
  { level: 10, color: "#ff6d00" },  // Legendary
  { level: 11, color: "#b9f2ff" },  // Diamond
  { level: 12, color: "#37474f" },  // Onyx
  { level: 13, color: "#b0bec5" },  // Platinum
  { level: 14, color: "#5c6bc0" },  // Tanzanite
  { level: 15, color: "#e91e63" },  // Mythic
];

export const getLevel = (level) => {
  return LEVEL_COLORS.find((t) => level === t.level) || LEVEL_COLORS[0];
};

// Width in px per size; height = width × 1.15 for a regular pointy-top hexagon
const SIZES = { small: 14, default: 22, large: 30 };

const HEX_CLIP =
  "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

export const Level = ({ level = 0, size = "default", children = null, style = {}, opacity = 1 }) => {
  if (level <= 0) return children ?? null;

  const levelData = getLevel(level);
  const w = SIZES[size] || SIZES.default;
  const h = Math.round(w * 1.15);

  const hexEl = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: w,
        height: h,
        clipPath: HEX_CLIP,
        backgroundColor: levelData.color,
        opacity: opacity,
        color: "#fff",
        fontSize: Math.round(w * 0.55),
        fontWeight: 700,
        lineHeight: 0.9,
        flexShrink: 0,
        ...(children
          ? {
              position: "absolute",
              bottom: -Math.round(h / 4),
              right: -Math.round(w / 4),
              zIndex: 1,
            }
          : {}),
        ...style,
      }}
    >
      {level}
    </span>
  );

  if (!children) return hexEl;

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      {children}
      {hexEl}
    </span>
  );
};
